/** Shared SIP employee role permission helpers. */

export function getEmployeePermissions(employeeData) {
  if (!employeeData?.permission?.permissions_json) return [];
  try {
    const parsed = JSON.parse(employeeData.permission.permissions_json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function isUrsSession() {
  return !!localStorage.getItem('access_token_urs');
}

export function isSipEmployee(employeeData) {
  return !!employeeData?.buddy;
}

export function hasHistoryView(employeeData) {
  if (isUrsSession()) return true;
  if (!isSipEmployee(employeeData)) return true;
  return getEmployeePermissions(employeeData).includes('history_view');
}

export function hasOnlineView(employeeData) {
  if (isUrsSession()) return true;
  if (!isSipEmployee(employeeData)) return true;
  if (!employeeData?.permission) return true;
  return getEmployeePermissions(employeeData).includes('online_view');
}

export function hasEmployeeTelegram(employeeData) {
  return (employeeData?.telegrams?.length ?? 0) > 0;
}

export function hasEmployeeWhatsApp(employeeData) {
  const plural = employeeData?.whatsapps;
  const singular = employeeData?.whatsapp;
  if (Array.isArray(plural) && plural.length > 0) return true;
  if (Array.isArray(singular) && singular.length > 0) return true;
  return false;
}

export function hasMessagingHistory(employeeData) {
  return hasEmployeeTelegram(employeeData) || hasEmployeeWhatsApp(employeeData);
}

export function isMessagingHistoryPath(pathname = '') {
  const p = pathname || '';
  return p.startsWith('/history_telegram') || p.startsWith('/history_whatsapp');
}

/** First Telegram or WhatsApp history route for this employee. */
export function getMessagingHistoryRoute(employeeData) {
  const tg = employeeData?.telegrams?.[0];
  if (tg?.id != null) return `/history_telegram/${tg.id}`;
  if (hasEmployeeWhatsApp(employeeData)) return '/history_whatsapp';
  return null;
}

const ROUTE_BY_PERMISSION = [
  ['employees_read', '/employees'],
  ['groups_read', '/groups'],
  ['ivr_read', '/ivrs'],
  ['did_read', '/main_number'],
  ['crm_integrations_manage', '/auto_call_list'],
];

/** First menu route the SIP employee is allowed to open. */
export function getFirstAllowedRoute(employeeData) {
  if (!isSipEmployee(employeeData)) return '/';
  if (!hasHistoryView(employeeData)) {
    const messaging = getMessagingHistoryRoute(employeeData);
    if (messaging) return messaging;
  }
  const perms = getEmployeePermissions(employeeData);
  for (const [perm, route] of ROUTE_BY_PERMISSION) {
    if (perms.includes(perm)) return route;
  }
  const messaging = getMessagingHistoryRoute(employeeData);
  if (messaging) return messaging;
  return '/profile';
}

export const HISTORY_PATH_PREFIXES = [
  '/history',
  '/graphic',
  '/operator_history',
  '/operator_report',
];

export function isHistoryPath(pathname = '') {
  const p = pathname || '';
  return HISTORY_PATH_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`)
  );
}

/** Whether the current user may play a call recording row. */
export function canListenToRecording(employeeData, row) {
  if (!row?.uniqueid) return false;
  if (row?.isAudio === false) return false;
  if (row?.duration != null && Number(row.duration) === 0) return false;

  const buddy = String(employeeData?.buddy || '').trim();
  if (!buddy) return true;

  const perms = getEmployeePermissions(employeeData);
  const hasListenAll = perms.includes('recordings_listen_all');
  const hasListenOwn = perms.includes('recordings_listen_own');
  if (!hasListenAll && !hasListenOwn) return false;
  if (hasListenAll) return true;

  const ext = String(row?.extension || '').trim();
  const dest = String(row?.destination || '').trim();
  const parts = [...ext.split(/[-/]/), ...dest.split(/[-/]/)]
    .map((p) => String(p).trim())
    .filter(Boolean);
  return parts.some((p) => p === buddy);
}
